import {
  ColDef,
  ColGroupDef,
  ICellEditorParams,
  ValueFormatterParams,
  ValueGetterParams,
  ValueSetterParams,
} from '@ag-grid-community/core'
import {
  getInlineEditColumnType,
  InlineEditDataType,
} from '@northfork/ag-grid-angular'
import { InlineEditDisplayOptions } from '@northfork/ng-basics'
import {
  BulkCellKind,
  BulkTransactionColumn,
  BulkTransactionRow,
} from '../../bulk-transaction/bulk-transaction.projection'
import {
  BULK_CELL_EDITOR,
  BulkCellEditorParams,
} from './bulk-cell-editor.component'
import {
  BulkCellRendererContext,
  bulkCellRenderer,
  bulkChangedRenderer,
  bulkStatusRenderer,
} from './bulk-cell-renderer'
import {
  BulkCollectionHeader,
  BulkGutterHeader,
  BulkHeaderContext,
  BulkItemBandHeader,
} from './bulk-grid-headers'
import {
  bulkBands,
  CHANGED_GUTTER_WIDTH,
  NUMBER_GUTTER_WIDTH,
  STATUS_GUTTER_WIDTH,
} from './bulk-transaction-grid'

/**
 * Column definitions for the ag-grid rendering of the Bulk Transactions grid.
 *
 * The design is unchanged, so every element it carries has to come out of a colDef rather than out
 * of the stylesheet's own grid: the band row becomes a column GROUP, the two sticky gutters become
 * pinned-left columns, and the banding and required tint become `cellClassRules` over the same
 * class names the hand-rolled grid already styles.
 */

/** What ag-grid holds per row. */
export interface BulkGridRow {
  /** Index into the view model. The row itself is projected on demand — see `rowAt`. */
  index: number
}

/** What the column builder needs from the component, without reaching into it. */
export interface BulkGridContext
  extends BulkCellRendererContext,
    BulkHeaderContext {
  /** The transaction at an index, projected on first ask. */
  rowAt(index: number): BulkTransactionRow | null
  /** What a cell reads, whether it is a field of the transaction or a value of one of its lines. */
  cellValueOf(index: number, field: string): string
  /** Whether a required cell is holding nothing — see the dialog's own rule for an Item cell. */
  gap(index: number, field: string): boolean
  /** Whether the cell at this row and column holds something other than what it opened with. */
  edited(index: number, field: string): boolean
  /** The stored value in the form it is TYPED in, which is not the form it is read in. */
  editValueOf(index: number, field: string): string
  /**
   * Write a typed value into a cell. Returns whether anything changed, which is what ag-grid uses
   * to decide whether to refresh — and what keeps a committed edit that changed nothing from
   * counting as a change.
   */
  setValue(index: number, field: string, value: string): boolean
  /**
   * A keystroke in a lookup cell: ask the endpoint what the text matches and offer the answers in the
   * value panel (PVAL-26685). Optional, so a context that cannot search leaves the cell free text.
   */
  searchLookup?(
    index: number,
    field: string,
    text: string,
    anchor: HTMLElement,
  ): void
}

/**
 * The kinds a cell can be typed into.
 *
 * The enum is absent on purpose: it opens an anchored panel rather than accepting typing, and its
 * editor lands with that panel. Until then an enum cell reads its value and shows its chevron, the
 * same as it did before ag-grid.
 */
const EDITABLE_KINDS = new Set<BulkCellKind>([
  'date',
  'number',
  'lookup',
  'text',
])

const STATUS_COL = 'bt-status'
const CHANGED_COL = 'bt-changed'
const NUMBER_COL = 'bt-number'

/**
 * The two gutter columns, by id.
 *
 * Exported because the clipboard has to drop them: they are the frame around the record, not part
 * of it, and a range the user dragged across them would otherwise copy a row number as if it were
 * a value — and paste into one.
 */
export const BULK_GUTTER_COLUMNS: ReadonlySet<string> = new Set([
  STATUS_COL,
  CHANGED_COL,
  NUMBER_COL,
])

/**
 * The two gutters, pinned left.
 *
 * `lockPosition` and `suppressMovable` because they are not columns of the record — they are the
 * frame around it, and a user who dragged the row number into the middle of the trade details would
 * have broken the grid rather than customised it.
 */
const gutterColumns = (context: BulkGridContext): ColDef[] => [
  {
    colId: STATUS_COL,
    headerName: '',
    headerTooltip:
      'Whether each transaction can be saved, and how many required values it is still missing',
    headerComponent: BulkGutterHeader,
    headerComponentParams: { icon: 'mdi-alert-circle-outline' },
    width: STATUS_GUTTER_WIDTH,
    minWidth: STATUS_GUTTER_WIDTH,
    maxWidth: STATUS_GUTTER_WIDTH,
    pinned: 'left',
    lockPosition: true,
    suppressMovable: true,
    resizable: false,
    sortable: false,
    cellClass: 'bt-ag-gutter bt-ag-gutter-status',
    headerClass: 'bt-ag-gutter-head',
    cellRenderer: bulkStatusRenderer(context),
  },
  {
    colId: CHANGED_COL,
    headerName: '',
    headerTooltip:
      'How many values each transaction now holds differently from the record',
    headerComponent: BulkGutterHeader,
    headerComponentParams: { icon: 'mdi-pencil' },
    width: CHANGED_GUTTER_WIDTH,
    minWidth: CHANGED_GUTTER_WIDTH,
    maxWidth: CHANGED_GUTTER_WIDTH,
    pinned: 'left',
    lockPosition: true,
    suppressMovable: true,
    resizable: false,
    sortable: false,
    cellClass: 'bt-ag-gutter bt-ag-gutter-changed',
    headerClass: 'bt-ag-gutter-head',
    cellRenderer: bulkChangedRenderer(context),
  },
  {
    colId: NUMBER_COL,
    headerName: '',
    headerTooltip: 'The position of each transaction in the selection',
    headerComponent: BulkGutterHeader,
    headerComponentParams: { icon: 'mdi-pound' },
    width: NUMBER_GUTTER_WIDTH,
    minWidth: NUMBER_GUTTER_WIDTH,
    maxWidth: NUMBER_GUTTER_WIDTH,
    pinned: 'left',
    lockPosition: true,
    suppressMovable: true,
    resizable: false,
    sortable: false,
    cellClass: 'bt-ag-gutter bt-ag-gutter-number',
    headerClass: 'bt-ag-gutter-head',
    // The number is the row's position, not a value it carries.
    valueGetter: (params: ValueGetterParams) =>
      params.node?.rowIndex == null ? '' : params.node.rowIndex + 1,
  },
]

/**
 * A date column, which helios renders with Northfork's inline editor rather than a calendar of its
 * own.
 *
 * The same four properties every other bulk grid in the app states for a date — the inline-edit
 * column type, its edit button, and `Date` as the editor's data type — so a date here behaves as a
 * date does in Cash Entry or Trade Entry, down to the control that opens.
 *
 * The value travels as the ISO form the API speaks, because `nf-datepicker` is what the editor
 * binds to and ISO is what it reads and writes. `valueFormatter` is what puts `MM/DD/YYYY` on
 * screen: the inline-edit renderer shows `valueFormatted` when there is one, so the resting cell
 * reads the way the design asks while the editor still gets the form it needs.
 *
 * The column type supplies the renderer and the editor, so this states neither — but it supplies
 * `cellClassRules` too, and a colDef's own would REPLACE them rather than add to them, which is why
 * `nf-inline-edit` is restated among ours below.
 */
const inlineDateColumn = (
  column: BulkTransactionColumn,
  context: BulkGridContext,
): Partial<ColDef> => ({
  type: getInlineEditColumnType(),
  cellRendererParams: { editButtonClickOnly: true } as InlineEditDisplayOptions,
  cellEditorParams: { dataType: InlineEditDataType.Date },
  valueGetter: (params: ValueGetterParams) =>
    context.editValueOf((params.data as BulkGridRow)?.index, column.field),
  valueFormatter: (params: ValueFormatterParams) =>
    context.cellValueOf((params.data as BulkGridRow)?.index, column.field),
})

/**
 * One data column.
 *
 * The value is read through `rowAt`, which projects the transaction on first ask — so ag-grid asking
 * for the cells it is about to draw is what decides how many records get projected. Sorting and
 * filtering are off for exactly that reason as well as the design's: either would call this getter
 * for every row in the selection and defeat the laziness.
 */
const dataColumn = (
  column: BulkTransactionColumn,
  context: BulkGridContext,
): ColDef => ({
  colId: column.field,
  field: column.field,
  headerName: column.label,
  width: column.width,
  minWidth: 74,
  sortable: false,
  filter: false,
  suppressMovable: true,
  resizable: true,
  headerClass: column.required
    ? 'bt-ag-head bt-ag-head-required'
    : 'bt-ag-head',
  ...(column.kind === 'collection'
    ? {
        headerComponent: BulkCollectionHeader,
        headerComponentParams: { bulk: context, collection: column.field },
      }
    : {}),
  // The kind, as a class, so the stylesheet can reach a cell by what it edits with. Only `date`
  // needs it today: a native date control cannot fit the width its resting text does.
  cellClass: `bt-ag-kind-${column.kind}`,
  valueGetter: (params: ValueGetterParams) =>
    context.cellValueOf((params.data as BulkGridRow)?.index, column.field),
  // A read-only field, a collection summary and a list this phase cannot resolve are all cells
  // that hold a value and refuse typing — the lock, the table glyph and the spinner each say which.
  editable: EDITABLE_KINDS.has(column.kind),
  ...(column.kind === 'date'
    ? {}
    : {
        cellRenderer: bulkCellRenderer(column.kind, context),
        cellEditor: BULK_CELL_EDITOR,
        cellEditorParams: (
          params: ICellEditorParams,
        ): Partial<BulkCellEditorParams> => {
          const index = (params.data as BulkGridRow)?.index
          return {
            kind: column.kind,
            // Armed on the STORED form, which is what reveals `1250` behind a displayed `1,250.00`.
            editValue: context.editValueOf(index, column.field),
            // Only a lookup reports its typing: the dialog decides whether this field searches at all.
            ...(column.kind === 'lookup'
              ? {
                  onType: (text: string, anchor: HTMLElement) =>
                    context.searchLookup?.(index, column.field, text, anchor),
                }
              : {}),
          }
        },
      }),
  valueSetter: (params: ValueSetterParams) =>
    context.setValue(
      (params.data as BulkGridRow)?.index,
      column.field,
      params.newValue == null ? '' : String(params.newValue),
    ),
  cellClassRules: {
    // Restated because the colDef's rules replace the inline-edit column type's own.
    'nf-inline-edit': () => column.kind === 'date',
    // Two tiers, alternating: a writable cell always sits brighter than a locked one and always
    // differs from the same column one row up.
    'bt-ag-striped': params => (params.node?.rowIndex ?? 0) % 2 === 1,
    'bt-ag-locked': () => column.kind === 'readOnly',
    // A column this transaction's schema does not carry — nothing to fill in, so it reads as out of
    // scope rather than as an empty cell someone forgot.
    'bt-ag-absent': params => {
      // An Item column is never absent: it addresses a line, and a row that has no such line yet
      // is a row with room for one rather than a row the column does not apply to.
      if (column.item) return false
      const row = context.rowAt((params.data as BulkGridRow)?.index)
      return !!row && !row.fields[column.field]
    },
    // A required cell holding nothing is the one thing that blocks Save, so it is marked where the
    // work is rather than only in the gutter and the tally.
    'bt-ag-gap': params =>
      column.required &&
      context.gap((params.data as BulkGridRow)?.index, column.field),
    'bt-ag-edited': params =>
      context.edited((params.data as BulkGridRow)?.index, column.field),
    'bt-ag-numeric': () =>
      column.kind === 'number' || column.kind === 'readOnly',
  },
  // LAST, so the date column's own value plumbing wins. Spread first, the generic `valueGetter`
  // below it silently replaced the ISO one and the picker was handed `09/03/2026` to bind to — it
  // reads and writes ISO, so the editor failed on open and the cell did nothing when clicked.
  ...(column.kind === 'date' ? inlineDateColumn(column, context) : {}),
})

/**
 * The whole column tree: the two gutters, then one group per run of columns sharing a display
 * group.
 *
 * Groups come from the same {@link bulkBands} the hand-rolled grid draws its band strip with, so
 * the two cannot disagree about where a band starts. A run repeated later in the document's order
 * gets a second group — `groupId` carries the run's position for exactly that reason, since ag-grid
 * merges adjacent groups that share an id.
 */
export function bulkGridColumnDefs(
  columns: readonly BulkTransactionColumn[],
  context: BulkGridContext,
): (ColDef | ColGroupDef)[] {
  const bands = bulkBands(columns)
  const groups: ColGroupDef[] = []
  let cursor = 0

  // The plus belongs on the end of a collection's run of Item bands, not on every one of them.
  const lastLine = new Map<string, number>()
  for (const column of columns) {
    if (!column.item) continue
    const held = lastLine.get(column.item.collection) ?? -1
    lastLine.set(column.item.collection, Math.max(held, column.item.line))
  }

  bands.forEach((band, position) => {
    const members = columns.slice(cursor, cursor + band.span)
    cursor += band.span
    const item = members[0]?.item
    groups.push({
      groupId: `${band.name}#${position}`,
      headerName: band.name,
      headerClass: 'bt-ag-band',
      marryChildren: true,
      ...(item
        ? {
            headerGroupComponent: BulkItemBandHeader,
            headerGroupComponentParams: {
              bulk: context,
              collection: item.collection,
              last: item.line === lastLine.get(item.collection),
            },
          }
        : {}),
      children: members.map(column => dataColumn(column, context)),
    })
  })

  // The gutters live under a band cell of their own. ag-grid leaves the group row BLANK above a
  // column that is not in a group, so three ungrouped gutters left a hole where the band strip
  // should run and the header read as starting a row lower than the rest of it.
  return [
    {
      groupId: 'bt-gutters',
      headerName: '',
      headerClass: 'bt-ag-band bt-ag-band-gutter',
      marryChildren: true,
      children: gutterColumns(context),
    },
    ...groups,
  ]
}
